import {
  Button,
  Tabs,
  Tab,
  Box,
  TextField,
  Grid,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { useNavigate } from "react-router-dom";
import "react-phone-input-2/lib/bootstrap.css";
import * as Yup from "yup";
import EditText from "../../components/editText";
import { showError, showToast } from "../../helpers/showToast";
import ManageFaq from "../../features/faqs/ManageFaq";
import { Loader } from "../../helpers";
import MainContainer from "../../layout/MainContainer";
import { useLazyGetCmsQuery, useUpdateCmsMutation } from "../../service/cms";
import { generateEncryptedKeyBody } from "utils/crypto";
import { CommonBody } from "types/General";

interface CmsFormValues {
  email: string;
  phone: string;
  aboutUs: string;
  privacyPolicy: string;
  dataDeletionPolicy: string;
  terms: string;
}

function CustomTabPanel(props: any) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCms = () => {
  const navigate = useNavigate();
  const [getCms] = useLazyGetCmsQuery();
  const [updateCMS] = useUpdateCmsMutation();
  const [cmsId, setCmsId] = useState("");
  const [phoneCode, setPhoneCode] = useState("+91");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [loading, setLoading] = useState(false);
  const [valueTabs, setValueTabs] = useState(0);

  const getCmsData = async () => {
    setLoading(true);
    try {
      const response = await getCms({}).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setCmsId(response?.data?._id || "");
        formik.setValues({
          email: response.data?.email || "",
          phone: response?.data?.phone || "",
          aboutUs: response?.data?.aboutUs || "",
          privacyPolicy: response?.data?.privacyPolicy || "",
          dataDeletionPolicy: response?.data?.dataDeletionPolicy || "",
          terms: response?.data?.terms || "",
        });
        setPhoneCode(response?.data?.dialCode || "+91");
        setPhoneDisplay(
          (response?.data?.dialCode || "") + (response?.data?.phone || "")
        );
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .required("Email is required")
      .test(
        "no-whitespace",
        "Email cannot be only spaces",
        (value: string | undefined) => !!value && value.trim().length > 0
      )
      .matches(
        /^(([^<>()[\].,;:\s@\"]+(\.[^<>()[\].,;:\s@\"]+)*)|(".+"))@(([^<>()[\].,;:\s@\"]+\.)+[^<>()[\].,;:\s@\"]{2,})$/i,
        "Enter a valid email address"
      ),
    phone: Yup.string()
      .required("Phone number is required")
      .test(
        "no-whitespace",
        "Phone cannot be only spaces",
        (value: string | undefined) => !!value && value.trim().length > 0
      )
      .min(6, "Phone number must be at least 6 characters")
      .max(16, "Phone number must be at most 16 characters"),
    aboutUs: Yup.string()
      .required("About Us is required")
      .test(
        "no-whitespace",
        "About Us cannot be only spaces",
        (value: string | undefined) => !!value && value.trim().length > 0
      ),
    privacyPolicy: Yup.string()
      .required("Privacy Policy is required")
      .test(
        "no-whitespace",
        "Privacy Policy cannot be only spaces",
        (value: string | undefined) => !!value && value.trim().length > 0
      ),
    dataDeletionPolicy: Yup.string()
      .required("Data Deletion Policy is required")
      .test(
        "no-whitespace",
        "Data Deletion Policy cannot be only spaces",
        (value: string | undefined) => !!value && value.trim().length > 0
      ),
    terms: Yup.string()
      .required("Terms & Conditions is required")
      .test(
        "no-whitespace",
        "Terms & Conditions cannot be only spaces",
        (value: string | undefined) => !!value && value.trim().length > 0
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      phone: "",
      aboutUs: "",
      privacyPolicy: "",
      dataDeletionPolicy: "",
      terms: "",
    },
    validationSchema,
    onSubmit: async (values: CmsFormValues) => {
      try {
        setLoading(true);
        const body = {
          email: values.email?.trim(),
          phone: values.phone?.trim(),
          privacyPolicy: values.privacyPolicy?.trim(),
          dataDeletionPolicy: values.dataDeletionPolicy?.trim(),
          aboutUs: values.aboutUs?.trim(),
          terms: values.terms?.trim(),
        };
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await updateCMS(encryptedBody).unwrap();
        setLoading(false);
        if (response.statusCode === 200) {
          showToast("CMS updated successfully!");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message);
      }
    },
  });

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    setPhoneDisplay(country?.dialCode + phone);
    setPhoneCode(country?.dialCode);
  };

  useEffect(() => {
    getCmsData();
  }, []);

  const handleSaveContact = async () => {
    await formik.setTouched({ email: true, phone: true });
    await formik.validateForm();
    const { email, phone } = formik.errors;
    if (email || phone) {
      showError("Please enter valid values for Contact fields.");
      return;
    }
    formik.handleSubmit();
  };

  const handleSaveAbout = async () => {
    await formik.setTouched({ aboutUs: true });
    await formik.validateForm();
    const { aboutUs } = formik.errors;
    if (aboutUs) {
      showError("Please enter valid values for About Us field.");
      return;
    }
    formik.handleSubmit();
  };

  const handleSavePrivacy = async () => {
    await formik.setTouched({ privacyPolicy: true });
    await formik.validateForm();
    const { privacyPolicy } = formik.errors;
    if (privacyPolicy) {
      showError("Please enter valid values for Privacy Policy field.");
      return;
    }
    formik.handleSubmit();
  };

  const handleSaveDataDeletion = async () => {
    await formik.setTouched({ dataDeletionPolicy: true });
    await formik.validateForm();
    const { dataDeletionPolicy } = formik.errors;
    if (dataDeletionPolicy) {
      showError("Please enter valid values for Data Deletion Policy field.");
      return;
    }
    formik.handleSubmit();
  };
  const handleSaveTerms = async () => {
    await formik.setTouched({ terms: true });
    await formik.validateForm();
    const { terms } = formik.errors;
    if (terms) {
      showError("Please enter valid values for Terms & Conditions field.");
      return;
    }
    formik.handleSubmit();
  };
  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>Manage CMS</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          CMS
        </p>
      </div>
      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Contact Us " {...a11yProps(0)} />
              <Tab label="About Us" {...a11yProps(1)} />
              <Tab label="Privacy Policy" {...a11yProps(2)} />
              <Tab label="Data Deletion Policy" {...a11yProps(3)} />
              <Tab label="Terms And Conditions" {...a11yProps(4)} />
              <Tab label="FAQ" {...a11yProps(5)} />
            </Tabs>
          </Box>
        </div>
      </div>
      <div className="cards">
        <form onSubmit={formik.handleSubmit}>
          <CustomTabPanel value={valueTabs} index={0}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label>Email Address</label>
                <TextField
                  className="text_field"
                  hiddenLabel
                  type={"email"}
                  name="email"
                  variant="outlined"
                  fullWidth
                  placeholder="Email"
                  id="email"
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  inputProps={{ maxLength: 40 }}
                  helperText={formik.touched.email && formik.errors.email}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                />
              </div>
              <div className="control_group w_50">
                <label>Phone Number</label>
                <PhoneInput
                  enableSearch={true}
                  value={phoneDisplay}
                  country={"us"}
                  inputClass="phoneInput"
                  inputStyle={{ width: "100%" }}
                  buttonClass="phoneBtn"
                  placeholder="Phone Number"
                  onChange={(phone, country) =>
                    handleChangePhone(phone, country)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <h6 className="err_msg">{formik.errors.phone}</h6>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div className="form_btn end">
              <Button
                className="btn btn_secondary"
                type="button"
                onClick={handleSaveContact}
              >
                Save
              </Button>
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={1}>
            <EditText
              content={formik.values.aboutUs}
              setContent={(content: string) =>
                formik.setFieldValue("aboutUs", content)
              }
            />
            {formik.touched.aboutUs && formik.errors.aboutUs && (
              <h6 className="err_msg">{formik.errors.aboutUs}</h6>
            )}
            <div className="form_btn end">
              <Button
                className="btn btn_secondary"
                type="button"
                onClick={handleSaveAbout}
              >
                Save
              </Button>
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={2}>
            <EditText
              content={formik.values.privacyPolicy}
              setContent={(content: string) =>
                formik.setFieldValue("privacyPolicy", content)
              }
            />
            {formik.touched.privacyPolicy && formik.errors.privacyPolicy && (
              <h6 className="err_msg">{formik.errors.privacyPolicy}</h6>
            )}
            <div className="form_btn end">
              <Button
                className="btn btn_secondary"
                type="button"
                onClick={handleSavePrivacy}
              >
                Save
              </Button>
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={3}>
            <EditText
              content={formik.values.dataDeletionPolicy}
              setContent={(content: string) =>
                formik.setFieldValue("dataDeletionPolicy", content)
              }
            />
            {formik.touched.dataDeletionPolicy &&
              formik.errors.dataDeletionPolicy && (
                <h6 className="err_msg">{formik.errors.dataDeletionPolicy}</h6>
              )}
            <div className="form_btn end">
              <Button
                className="btn btn_secondary"
                type="button"
                onClick={handleSaveDataDeletion}
              >
                Save
              </Button>
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={4}>
            <EditText
              content={formik.values.terms}
              setContent={(content: string) =>
                formik.setFieldValue("terms", content)
              }
            />
            {formik.touched.terms && formik.errors.terms && (
              <h6 className="err_msg">{formik.errors.terms}</h6>
            )}
            <div className="form_btn end">
              <Button
                className="btn btn_secondary"
                type="button"
                onClick={handleSaveTerms}
              >
                Save
              </Button>
            </div>
          </CustomTabPanel>
          <CustomTabPanel value={valueTabs} index={5}>
            <ManageFaq />
          </CustomTabPanel>
        </form>
      </div>
    </MainContainer>
  );
};

export default ManageCms;
